import {Alert, Button, Group, Select, Text} from '@mantine/core'
import {IconBuildingWarehouse} from '@tabler/icons-react'
import {useState} from 'react'

import type {DispatchYard} from '../dispatch/schemas'

type JobYardPanelProps = {
    yardName: string | null
    yards: DispatchYard[]
    canMove?: boolean
    onSetYard: (yardId: string) => Promise<void>
}

export const BORROW_DRIVERS_NOTE =
    'Orders stay with the yard they were entered under. Short on drivers? Borrow them from the other yard.'

export function JobYardPanel({yardName, yards, canMove = false, onSetYard}: JobYardPanelProps) {
    const [yardId, setYardId] = useState<string | null>(null)
    const [saving, setSaving] = useState(false)

    async function save() {
        if (yardId == null) return
        setSaving(true)
        try {
            await onSetYard(yardId)
        } finally {
            setSaving(false)
        }
    }

    if (yardName != null) {
        return (
            <Group gap="xs" align="center">
                <Text size="sm" c="dimmed" data-testid="job-yard">
                    {yardName} board
                </Text>
                {canMove ? (
                    <>
                        <Select
                            aria-label="Move to yard"
                            placeholder="Move to"
                            size="xs"
                            data={yards
                                .filter((yard) => yard.name !== yardName)
                                .map((yard) => ({value: yard.id, label: yard.name}))}
                            value={yardId}
                            onChange={setYardId}
                            w={150}
                        />
                        <Button
                            size="xs"
                            variant="default"
                            disabled={yardId == null}
                            loading={saving}
                            onClick={() => void save()}
                        >
                            Move
                        </Button>
                    </>
                ) : (
                    <Text size="xs" c="dimmed" data-testid="borrow-drivers-note">
                        {BORROW_DRIVERS_NOTE}
                    </Text>
                )}
            </Group>
        )
    }

    return (
        <Alert
            color="yellow"
            variant="light"
            icon={<IconBuildingWarehouse size={20} />}
            title="No yard set"
            data-testid="job-yard-panel"
        >
            <Text size="sm" mb="xs">
                Shows on every board until a yard is set
            </Text>
            <Group gap="xs" align="flex-end">
                <Select
                    aria-label="Yard"
                    placeholder="Choose a yard"
                    data={yards.map((yard) => ({value: yard.id, label: yard.name}))}
                    value={yardId}
                    onChange={setYardId}
                    w={180}
                />
                <Button disabled={yardId == null} loading={saving} onClick={() => void save()}>
                    Set yard
                </Button>
            </Group>
        </Alert>
    )
}
